import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Sparkles, Calendar, ArrowRight, Star, Zap } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Product } from '../../types/models';
import { formatZAR } from '../../utils/currency';
import { Button } from '../ui/Button';
import { useCart } from '../../contexts/CartContext';
interface SpecialEventModalProps {
  isOpen: boolean;
  onClose: () => void;
  products: Product[];
}
export function SpecialEventModal({
  isOpen,
  onClose,
  products
}: SpecialEventModalProps) {
  const navigate = useNavigate();
  const {
    addToCart
  } = useCart();
  const handleAddToCart = (product: Product) => {
    addToCart(product);
  };
  const handleShopNow = () => {
    onClose();
  };
  if (!isOpen || products.length === 0) return null;
  return <AnimatePresence data-id="element-624">
      <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/70 backdrop-blur-md" data-id="element-625">
        <motion.div initial={{
        opacity: 0,
        scale: 0.8,
        y: 50
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.8,
        y: 50
      }} transition={{
        type: 'spring',
        damping: 25,
        stiffness: 300
      }} className="bg-white rounded-3xl w-full max-w-5xl max-h-[90vh] overflow-hidden shadow-2xl relative" data-id="element-626">
          {/* Animated Background Elements */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none" data-id="element-627">
            <motion.div animate={{
            scale: [1, 1.2, 1],
            rotate: [0, 90, 0],
            opacity: [0.3, 0.5, 0.3]
          }} transition={{
            duration: 8,
            repeat: Infinity,
            ease: 'easeInOut'
          }} className="absolute -top-20 -right-20 w-64 h-64 bg-gradient-to-br from-rose-400 to-purple-400 rounded-full blur-3xl" data-id="element-628" />
            <motion.div animate={{
            scale: [1, 1.3, 1],
            rotate: [0, -90, 0],
            opacity: [0.2, 0.4, 0.2]
          }} transition={{
            duration: 10,
            repeat: Infinity,
            ease: 'easeInOut',
            delay: 1
          }} className="absolute -bottom-20 -left-20 w-64 h-64 bg-gradient-to-br from-yellow-400 to-rose-400 rounded-full blur-3xl" data-id="element-629" />
          </div>

          {/* Header */}
          <div className="relative bg-gradient-to-br from-rose-500 via-purple-500 to-pink-600 p-8 text-white overflow-hidden" data-id="element-630">
            {/* Floating Stars */}
            <motion.div animate={{
            y: [0, -10, 0],
            rotate: [0, 360]
          }} transition={{
            duration: 3,
            repeat: Infinity,
            ease: 'easeInOut'
          }} className="absolute top-4 left-8" data-id="element-631">
              <Star className="w-6 h-6 text-yellow-300 fill-yellow-300" data-id="element-632" />
            </motion.div>
            <motion.div animate={{
            y: [0, -15, 0],
            rotate: [0, -360]
          }} transition={{
            duration: 4,
            repeat: Infinity,
            ease: 'easeInOut',
            delay: 0.5
          }} className="absolute top-12 right-16" data-id="element-633">
              <Sparkles className="w-5 h-5 text-yellow-200" data-id="element-634" />
            </motion.div>
            <motion.div animate={{
            y: [0, -8, 0],
            rotate: [0, 360]
          }} transition={{
            duration: 3.5,
            repeat: Infinity,
            ease: 'easeInOut',
            delay: 1
          }} className="absolute bottom-8 left-20" data-id="element-635">
              <Zap className="w-5 h-5 text-yellow-300 fill-yellow-300" data-id="element-636" />
            </motion.div>

            <button onClick={onClose} className="absolute top-4 right-4 p-2 rounded-full bg-white/20 hover:bg-white/30 transition-all hover:rotate-90 duration-300 z-10" data-id="element-637">
              <X size={24} data-id="element-638" />
            </button>

            <motion.div initial={{
            y: 20,
            opacity: 0
          }} animate={{
            y: 0,
            opacity: 1
          }} transition={{
            delay: 0.2
          }} className="relative z-10" data-id="element-639">
              <div className="flex items-center gap-4 mb-4" data-id="element-640">
                <motion.div animate={{
                scale: [1, 1.1, 1],
                rotate: [0, 5, -5, 0]
              }} transition={{
                duration: 2,
                repeat: Infinity,
                ease: 'easeInOut'
              }} className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center backdrop-blur-sm border-2 border-white/30" data-id="element-641">
                  <Sparkles className="w-8 h-8" data-id="element-642" />
                </motion.div>
                <div data-id="element-643">
                  <motion.h2 initial={{
                  x: -20,
                  opacity: 0
                }} animate={{
                  x: 0,
                  opacity: 1
                }} transition={{
                  delay: 0.3
                }} className="text-4xl font-serif font-bold mb-1" data-id="element-644">
                    ✨ Special Event! ✨
                  </motion.h2>
                  <motion.p initial={{
                  x: -20,
                  opacity: 0
                }} animate={{
                  x: 0,
                  opacity: 1
                }} transition={{
                  delay: 0.4
                }} className="text-rose-100 text-lg" data-id="element-645">
                    Limited time exclusive products just for you
                  </motion.p>
                </div>
              </div>

              <motion.div initial={{
              scale: 0
            }} animate={{
              scale: 1
            }} transition={{
              delay: 0.5,
              type: 'spring'
            }} className="inline-flex items-center gap-2 px-4 py-2 bg-white/20 rounded-full backdrop-blur-sm border border-white/30" data-id="element-646">
                <Calendar className="w-4 h-4" data-id="element-647" />
                <span className="text-sm font-medium" data-id="element-648">
                  Don't miss out - Limited availability!
                </span>
              </motion.div>
            </motion.div>
          </div>

          {/* Products Grid */}
          <div className="p-8 overflow-y-auto max-h-[calc(90vh-280px)] relative" data-id="element-649">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-650">
              {products.map((product, index) => <motion.div key={product.id} initial={{
              opacity: 0,
              y: 30,
              scale: 0.9
            }} animate={{
              opacity: 1,
              y: 0,
              scale: 1
            }} transition={{
              delay: index * 0.1,
              type: 'spring',
              damping: 20,
              stiffness: 300
            }} whileHover={{
              y: -8,
              scale: 1.02
            }} className="group bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 border-2 border-transparent hover:border-rose-300 relative" data-id="element-651">
                  {/* Shine effect on hover */}
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000 pointer-events-none" data-id="element-652" />

                  <div className="relative aspect-square overflow-hidden bg-gradient-to-br from-gray-50 to-gray-100" data-id="element-653">
                    <img src={product.image} alt={product.name} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" data-id="element-654" />
                    <motion.div initial={{
                  scale: 0,
                  rotate: -180
                }} animate={{
                  scale: 1,
                  rotate: 0
                }} transition={{
                  delay: index * 0.1 + 0.3,
                  type: 'spring'
                }} className="absolute top-3 left-3 bg-gradient-to-r from-rose-500 to-purple-500 text-white px-3 py-1.5 rounded-full text-xs font-bold shadow-lg flex items-center gap-1.5" data-id="element-655">
                      <motion.div animate={{
                    rotate: [0, 360]
                  }} transition={{
                    duration: 2,
                    repeat: Infinity,
                    ease: 'linear'
                  }} data-id="element-656">
                        <Sparkles className="w-3.5 h-3.5" data-id="element-657" />
                      </motion.div>
                      SPECIAL
                    </motion.div>
                  </div>

                  <div className="p-5" data-id="element-658">
                    <div className="text-xs font-bold text-rose-500 mb-1 uppercase tracking-wider" data-id="element-659">
                      {product.category}
                    </div>
                    <h3 className="font-serif text-lg font-bold text-gray-900 mb-2 line-clamp-2 min-h-[3.5rem] group-hover:text-rose-600 transition-colors" data-id="element-660">
                      {product.name}
                    </h3>

                    {product.specialEventEndDate && <motion.div initial={{
                  opacity: 0,
                  x: -10
                }} animate={{
                  opacity: 1,
                  x: 0
                }} transition={{
                  delay: index * 0.1 + 0.4
                }} className="flex items-center gap-2 text-xs text-amber-700 mb-3 bg-gradient-to-r from-amber-50 to-yellow-50 px-3 py-2 rounded-lg border border-amber-200" data-id="element-661">
                        <Calendar className="w-3.5 h-3.5 text-amber-600" data-id="element-662" />
                        <span className="font-medium" data-id="element-663">
                          Until{' '}
                          {new Date(product.specialEventEndDate).toLocaleDateString('en-US', {
                      month: 'short',
                      day: 'numeric'
                    })}
                        </span>
                      </motion.div>}

                    <div className="flex items-center justify-between mb-4" data-id="element-664">
                      <span className="text-2xl font-bold bg-gradient-to-r from-rose-600 to-purple-600 bg-clip-text text-transparent" data-id="element-665">
                        {formatZAR(product.price)}
                      </span>
                      {product.stock > 0 ? <span className="text-xs text-green-600 font-semibold bg-green-50 px-2 py-1 rounded-full" data-id="element-666">
                          In Stock
                        </span> : <span className="text-xs text-red-600 font-semibold bg-red-50 px-2 py-1 rounded-full" data-id="element-667">
                          Sold Out
                        </span>}
                    </div>

                    <Button onClick={() => handleAddToCart(product)} disabled={product.stock === 0} className="w-full" size="sm" data-id="element-668">
                      Add to Cart
                    </Button>
                  </div>
                </motion.div>)}
            </div>
          </div>

          {/* Footer */}
          <motion.div initial={{
          y: 50,
          opacity: 0
        }} animate={{
          y: 0,
          opacity: 1
        }} transition={{
          delay: 0.6
        }} className="p-6 bg-gradient-to-r from-gray-50 to-rose-50 border-t border-gray-200 relative overflow-hidden" data-id="element-669">
            {/* Animated gradient line */}
            <motion.div animate={{
            x: ['-100%', '100%']
          }} transition={{
            duration: 3,
            repeat: Infinity,
            ease: 'linear'
          }} className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-rose-500 to-transparent" data-id="element-670" />

            <div className="flex flex-col sm:flex-row gap-4 justify-between items-center relative z-10" data-id="element-671">
              <div className="text-center sm:text-left" data-id="element-672">
                <p className="font-semibold text-gray-900 mb-1" data-id="element-673">
                  🎉 Exclusive Offers Inside!
                </p>
                <p className="text-sm text-gray-600" data-id="element-674">
                  These special products won't last long - grab yours now!
                </p>
              </div>
              <div className="flex gap-3 w-full sm:w-auto" data-id="element-675">
                <Button variant="outline" onClick={onClose} className="flex-1 sm:flex-none" data-id="element-676">
                  Continue Shopping
                </Button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      </motion.div>
    </AnimatePresence>;
}